/**
 * useProspectosPendientes — hook global del admin que mantiene el conteo
 * de asociados en estado prospecto+pendiente y dispara notificaciones
 * cuando llega uno nuevo.
 *
 * Por qué existe:
 *   El operador tiene que enterarse SÍ O SÍ que llegó un nuevo asociado
 *   esperando aprobación. El badge del sidebar es muy sutil; el banner
 *   del dashboard solo se ve si el operador entra al panel. Este hook
 *   centraliza:
 *
 *   1. Conteo en vivo (Supabase Realtime sobre tabla 'asociado').
 *   2. Document title con contador (la pestaña muestra "(3) NeoCore").
 *   3. Chime de audio cuando entra un nuevo prospecto.
 *   4. Browser Notification (opt-in) si el operador autorizó permisos.
 *
 * El hook se monta una sola vez en AppShell. Cualquier componente que
 * necesite el conteo (banner, badge, etc) puede llamarlo y va a recibir
 * el mismo valor — pero idealmente solo AppShell lo monta y los demás
 * leen via context o pasan props.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'

// Chime "doorbell" — dos tonos descendentes E5 → C5 estilo timbre. Mucho más
// llamativo que un solo tono A5 porque el patrón ding-dong activa la heurística
// "alguien llegó" en lugar de "ping de notificación que ignoro". Volumen al
// 35% para que sea audible sobre música/llamadas sin asustar.
const CHIME_NOTE_HIGH = 659.25 // E5
const CHIME_NOTE_LOW = 523.25  // C5
const CHIME_NOTE_DURATION_MS = 280
const CHIME_GAP_MS = 60
const CHIME_VOLUME = 0.35

const TITLE_BASE = 'NeoCore Admin'
const NOTIF_PERMISSION_KEY = 'neocoop-admin-notif-asked'

// -----------------------------------------------------------------------------
// AudioContext "primed" globalmente — Chrome (y la mayoría de browsers) bloquean
// WebAudio hasta que el usuario hace algún gesto (click/keydown/touch).
//
// Estrategia de priming + buffer:
//   - Lazy-create el AudioContext la primera vez (queda suspended hasta gesto).
//   - Si playChime() se llama mientras está suspended, BUFFEAMOS la intención
//     en `pendingChimes` (en lugar de programar osciladores en el aire que se
//     pierden silenciosamente).
//   - El primer gesto del usuario engancha resume() — al completar, drainamos
//     el buffer reproduciendo los chimes pendientes (uno solo, no spam).
//   - playChime() después del primer gesto suena al instante.
//
// Logs prefijados con [neocoop-banner] para que Hugo pueda diagnosticar abriendo
// la consola del browser sin tener que leer código.
// -----------------------------------------------------------------------------

let cachedAudioCtx: AudioContext | null = null
let primedListenersAttached = false
let pendingChimes = 0
const LOG_PREFIX = '[neocoop-banner]'

function getAudioContext(): AudioContext | null {
  if (cachedAudioCtx) return cachedAudioCtx
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext
    if (!Ctx) return null
    const ctx: AudioContext = new Ctx()
    cachedAudioCtx = ctx
    console.log(LOG_PREFIX, 'AudioContext creado, state:', ctx.state)
    return ctx
  } catch (e) {
    console.warn(LOG_PREFIX, 'AudioContext no soportado', e)
    return null
  }
}

function drainPendingChimes() {
  if (pendingChimes <= 0) return
  console.log(LOG_PREFIX, 'Drain de chimes pendientes:', pendingChimes)
  pendingChimes = 0
  // Tocar UNO solo aunque hayan varios pendientes — no queremos spam ding-dong.
  playChimeNow()
}

/** Llamado por el efecto del hook — engancha el primer gesto del usuario para
 * desbloquear/resumir el AudioContext. Idempotente. */
function attachPrimingListeners() {
  if (primedListenersAttached) return
  if (typeof window === 'undefined') return
  primedListenersAttached = true
  console.log(LOG_PREFIX, 'Listeners de priming enganchados (esperando primer gesto)')

  const prime = () => {
    const ctx = getAudioContext()
    if (!ctx) return
    if (ctx.state === 'suspended') {
      ctx
        .resume()
        .then(() => {
          console.log(LOG_PREFIX, 'AudioContext resumed, state:', ctx.state)
          drainPendingChimes()
        })
        .catch((err) => console.warn(LOG_PREFIX, 'resume() rechazó', err))
    } else {
      drainPendingChimes()
    }
    window.removeEventListener('click', prime, true)
    window.removeEventListener('keydown', prime, true)
    window.removeEventListener('touchstart', prime, true)
  }
  window.addEventListener('click', prime, true)
  window.addEventListener('keydown', prime, true)
  window.addEventListener('touchstart', prime, true)
}

type Estado = 'prospecto' | 'pendiente'

export type ProspectoPendienteRow = {
  id: string
  dpi: string
  nombre_completo: string
  estado_asociado: Estado
  created_at: string
  updated_at: string | null
}

export type UseProspectosPendientesResult = {
  count: number
  /** Solo los pendientes (subieron comprobante, listos para aprobar). */
  pendienteCount: number
  /** Solo los prospectos (sin comprobante todavía). */
  prospectoCount: number
  loading: boolean
  /** IDs en estado prospecto+pendiente (para detectar nuevos). */
  ids: string[]
  /** Refresh manual del conteo (raramente necesario, el realtime ya lo hace). */
  refresh: () => Promise<void>
  /** Pedir permiso para Notifications nativas (opt-in del operador). */
  requestNotificationPermission: () => Promise<NotificationPermission>
  /** Estado actual del permiso de notificaciones. */
  notificationPermission: NotificationPermission | 'unsupported'
  /** Tocar el chime manualmente (para botón "Probar"). Devuelve true si sonó. */
  testChime: () => boolean
}

/**
 * Versión "interna" — programa los osciladores asumiendo que el AudioContext
 * ya está corriendo. No revisa el state. Usar solo después de verificar que
 * el ctx está running (manualmente o vía drainPendingChimes).
 */
function playChimeNow(): boolean {
  try {
    const ctx = getAudioContext()
    if (!ctx) return false
    if (ctx.state !== 'running') {
      console.warn(LOG_PREFIX, 'playChimeNow llamado con ctx no-running:', ctx.state)
    }

    const noteDur = CHIME_NOTE_DURATION_MS / 1000
    const gap = CHIME_GAP_MS / 1000
    const t0 = ctx.currentTime

    const playTone = (freq: number, startAt: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'triangle'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0, startAt)
      gain.gain.linearRampToValueAtTime(CHIME_VOLUME, startAt + 0.015)
      gain.gain.linearRampToValueAtTime(CHIME_VOLUME * 0.55, startAt + 0.10)
      gain.gain.exponentialRampToValueAtTime(0.0001, startAt + noteDur)
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start(startAt)
      osc.stop(startAt + noteDur + 0.02)

      // Armónica octava arriba — le da brillo de campana
      const oscHi = ctx.createOscillator()
      const gainHi = ctx.createGain()
      oscHi.type = 'sine'
      oscHi.frequency.value = freq * 2
      gainHi.gain.setValueAtTime(0, startAt)
      gainHi.gain.linearRampToValueAtTime(CHIME_VOLUME * 0.20, startAt + 0.012)
      gainHi.gain.exponentialRampToValueAtTime(0.0001, startAt + noteDur * 0.8)
      oscHi.connect(gainHi)
      gainHi.connect(ctx.destination)
      oscHi.start(startAt)
      oscHi.stop(startAt + noteDur + 0.02)
    }

    playTone(CHIME_NOTE_HIGH, t0)
    playTone(CHIME_NOTE_LOW, t0 + noteDur + gap)
    return true
  } catch (e) {
    console.warn(LOG_PREFIX, 'playChimeNow falló', e)
    return false
  }
}

/**
 * Reproduce un "ding-dong" de timbre. Maneja correctamente el autoplay block:
 *   - Si el AudioContext está running → suena al instante.
 *   - Si está suspended → buffea la intención y suena cuando el operador
 *     haga su primer gesto (que también primará el contexto).
 *
 * Retorna true si el chime sonó O fue buffereado correctamente. False solo
 * si WebAudio no existe (browser muy viejo).
 */
export function playChime(): boolean {
  const ctx = getAudioContext()
  if (!ctx) return false

  if (ctx.state === 'running') {
    return playChimeNow()
  }

  // Suspended: intentar resumir (no funciona si no hubo gesto, pero igual lo
  // intentamos por si el browser ya considera el ctx primed) y bufferear.
  pendingChimes += 1
  console.log(
    LOG_PREFIX,
    'Chime buffereado (ctx suspended). Pendientes:',
    pendingChimes,
    '— sonará en el primer click/keydown del operador.',
  )
  ctx
    .resume()
    .then(() => {
      if (ctx.state === 'running') {
        console.log(LOG_PREFIX, 'resume() exitoso sin gesto explícito')
        drainPendingChimes()
      }
    })
    .catch(() => {
      // Espera al gesto del usuario — los listeners de priming drenan el buffer
    })
  return true
}

/**
 * Dispara una browser Notification (si el operador dio permiso). Si no,
 * silencioso — el banner sigue siendo visible en la app.
 */
function fireBrowserNotification(count: number) {
  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') return
  try {
    const title = count === 1
      ? 'Nuevo asociado esperando aprobación'
      : `${count} asociados esperando aprobación`
    const body = 'Revisalos en NeoCore → Asociados.'
    const n = new Notification(title, {
      body,
      icon: '/brand/logo-vertical.png',
      badge: '/brand/logo-vertical.png',
      tag: 'neocoop-prospecto-pendiente',
      requireInteraction: false,
    })
    n.onclick = () => {
      window.focus()
      window.location.href = '/neocore/asociados?estado=pendiente'
      n.close()
    }
  } catch {
    // ignore
  }
}

export function useProspectosPendientes(): UseProspectosPendientesResult {
  const [count, setCount] = useState(0)
  const [pendienteCount, setPendienteCount] = useState(0)
  const [prospectoCount, setProspectoCount] = useState(0)
  const [ids, setIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission | 'unsupported'>(
      typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
    )

  // Tracking de IDs vistos para detectar realmente NUEVOS prospectos
  // (porque realtime puede disparar UPDATEs de asociados que ya estaban
  // en estado pendiente y no queremos chime en ese caso).
  const knownIdsRef = useRef<Set<string>>(new Set())
  const initializedRef = useRef(false)

  const refresh = useCallback(async () => {
    const { data, error } = await supabase
      .from('asociado')
      .select('id, estado_asociado')
      .in('estado_asociado', ['prospecto', 'pendiente'])

    if (error) {
      console.warn(LOG_PREFIX, 'refresh() error', error)
      setLoading(false)
      return
    }

    const rows = (data ?? []) as Array<{ id: string; estado_asociado: Estado }>
    const newIds = rows.map((r) => r.id)
    const newIdSet = new Set(newIds)
    const pendientes = rows.filter((r) => r.estado_asociado === 'pendiente').length
    const prospectos = rows.filter((r) => r.estado_asociado === 'prospecto').length

    let nuevosNuevos = 0
    if (initializedRef.current) {
      for (const id of newIds) {
        if (!knownIdsRef.current.has(id)) nuevosNuevos++
      }
    }

    console.log(LOG_PREFIX, 'refresh()', {
      total: newIds.length,
      pendientes,
      prospectos,
      nuevosDetectados: nuevosNuevos,
      yaInicializado: initializedRef.current,
    })

    knownIdsRef.current = newIdSet
    setIds(newIds)
    setCount(newIds.length)
    setPendienteCount(pendientes)
    setProspectoCount(prospectos)
    setLoading(false)
    initializedRef.current = true

    if (nuevosNuevos > 0) {
      console.log(LOG_PREFIX, '🔔 Disparando chime +', nuevosNuevos, 'nuevo(s)')
      playChime()
      fireBrowserNotification(newIds.length)
    }
  }, [])

  // Carga inicial + priming del AudioContext en el primer gesto del usuario.
  // Sin esto Chrome bloquea WebAudio por su política de autoplay y el chime
  // falla silenciosamente en lugar de sonar.
  useEffect(() => {
    refresh()
    attachPrimingListeners()
  }, [refresh])

  // Realtime — INSERT y UPDATE sobre la tabla asociado. Cualquier cambio
  // refresca el conteo. El refresh interno detecta IDs realmente nuevos.
  useEffect(() => {
    console.log(LOG_PREFIX, 'Subscribing al canal Realtime de asociado')
    const channel = supabase
      .channel('admin-prospectos-pendientes')
      .on(
        'postgres_changes' as any,
        { event: 'INSERT', schema: 'public', table: 'asociado' },
        (payload: any) => {
          console.log(LOG_PREFIX, 'Realtime INSERT recibido', payload?.new)
          refresh().catch(() => {})
        },
      )
      .on(
        'postgres_changes' as any,
        { event: 'UPDATE', schema: 'public', table: 'asociado' },
        (payload: any) => {
          console.log(
            LOG_PREFIX,
            'Realtime UPDATE recibido',
            'estado:',
            payload?.new?.estado_asociado,
          )
          refresh().catch(() => {})
        },
      )
      .subscribe((status: string) => {
        console.log(LOG_PREFIX, 'Canal Realtime status:', status)
      })

    return () => {
      console.log(LOG_PREFIX, 'Removiendo canal Realtime')
      supabase.removeChannel(channel).catch(() => {})
    }
  }, [refresh])

  // Fallback de polling cada 15s (antes 60s) — failsafe rápido por si
  // Realtime no se conecta o se desconecta. 15s es suficientemente granular
  // para que el operador no espere más de eso, y suficientemente espaciado
  // para no martillar la BD.
  useEffect(() => {
    const t = setInterval(() => {
      refresh().catch(() => {})
    }, 15_000)
    return () => clearInterval(t)
  }, [refresh])

  // Document title — actualizamos la pestaña del navegador con el contador.
  // Aunque el operador no esté en la pestaña, va a ver "(3) NeoCore Admin"
  // en su lista de tabs.
  useEffect(() => {
    if (count > 0) {
      document.title = `(${count}) ${TITLE_BASE}`
    } else {
      document.title = TITLE_BASE
    }
    return () => {
      document.title = TITLE_BASE
    }
  }, [count])

  const requestNotificationPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'denied' as NotificationPermission
    try {
      localStorage.setItem(NOTIF_PERMISSION_KEY, '1')
    } catch {
      // ignore
    }
    const result = await Notification.requestPermission()
    setNotificationPermission(result)
    return result
  }, [])

  return {
    count,
    pendienteCount,
    prospectoCount,
    loading,
    ids,
    refresh,
    requestNotificationPermission,
    notificationPermission,
    testChime: playChime,
  }
}
